import { useLayoutEffect, useRef } from "react";

import { animate, duration, easing } from "@/shared/lib";
import { tactileSequence } from "@/shared/telegram";

/**
 * Одометр для денег и процентов (FE-13).
 *
 * Принимает УЖЕ отформатированную строку — ту самую, что отдал `formatMoney`
 * или `formatPercent`, — и анимирует только изменившиеся глифы. Никакой
 * арифметики, никакого промежуточного «досчёта» от старой суммы к новой:
 * правило ТЗ №5 запрещает клиенту считать деньги, а «красивый счётчик»,
 * перебирающий значения, — это ровно счёт. Здесь меняется только положение
 * символов на экране.
 *
 * Сравнение идёт по позиции в строке. Когда меняется разрядность
 * (`999,99` → `1 000,00`), совпадений почти нет и прокручивается вся сумма —
 * это и есть правильное поведение: изменилось действительно всё.
 *
 * Отклик (FE-14) включается пропом `tick` и только там, где сумма — событие:
 * баланс после покупки. По одному тику на разряд получился бы треск, поэтому
 * их ровно три; семейство `selectionChanged` выбрано за то, что серия из него
 * не сливается в гудение. Тики отложены на длительность перехода экрана —
 * там они попадают в паузу между разлётом салюта и подтверждением успеха, а
 * не в их гущу (см. хореографию в shared/telegram/fanfare.ts).
 */
interface RollingNumberProps {
  value: string;
  className?: string;
  /** Сопроводить прокрутку тихими тиками. Только для сумм-событий. */
  tick?: boolean;
}

/** Шаг задержки между разрядами. Держит волну слева направо читаемой. */
const CHAR_DELAY_MS = 24;
const MAX_DELAYED_CHARS = 8;

/** Тиков на одну прокрутку. Три — это ритм, восемь — треск. */
const TICKS = 3;

export function RollingNumber({ value, className, tick = false }: RollingNumberProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const previous = useRef<string | undefined>(undefined);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;

    const before = previous.current;
    previous.current = value;

    const chars = root.querySelectorAll<HTMLElement>("[data-char]");

    // Условие то же, по которому ниже прокручиваются символы: первое
    // появление катится целиком, дальше — только при смене суммы.
    const rolling = before === undefined || before !== value;
    const stopTicks =
      tick && rolling
        ? tactileSequence(
            Array.from({ length: TICKS }, (_, index) => ({
              at:
                duration("page") +
                (index * Math.min(chars.length, MAX_DELAYED_CHARS) * CHAR_DELAY_MS) / TICKS,
              intent: "select" as const,
            })),
          )
        : undefined;

    chars.forEach((element, index) => {
      // Первое появление прокручивается целиком, дальше — только то, что
      // действительно изменилось: иначе каждое обновление данных
      // перетряхивало бы всю сумму.
      if (before !== undefined && before[index] === value[index]) return;

      animate(
        element,
        [
          { transform: "translateY(0.55em)", opacity: 0 },
          { transform: "translateY(0)", opacity: 1 },
        ],
        {
          duration: duration("base"),
          easing: easing("out-quint"),
          delay: Math.min(index, MAX_DELAYED_CHARS) * CHAR_DELAY_MS,
          fill: "backwards",
        },
      );
    });

    return stopTicks;
  }, [value, tick]);

  return (
    <span className={className}>
      {/* Разбитая на глифы сумма для скринридера — набор отдельных символов,
          поэтому целая строка остаётся в разметке отдельным узлом. */}
      <span className="sr-only">{value}</span>

      <span ref={ref} aria-hidden="true">
        {[...value].map((char, index) => (
          <span
            // Ключ по позиции намеренно: узел на позиции должен
            // переиспользоваться, иначе браузеру нечего анимировать —
            // символ просто заменится.
            key={index}
            data-char
            className="inline-block whitespace-pre"
          >
            {char}
          </span>
        ))}
      </span>
    </span>
  );
}
